import React, { useState, useEffect, useRef } from 'react'
import {
  X,
  Upload,
  Link,
  Play,
  Search,
  Loader2,
  AlertCircle,
  Image as ImageIcon,
  FileVideo,
  CheckCircle2,
} from 'lucide-react'
import { Button } from '@trafiflow/shared-ui'
import { fbAdsService, type FbAd } from '@/services/fb-ads'

// ─── Types ────────────────────────────────────────────────────────────────────

type TabId = 'browse' | 'upload'

interface AdSourceModalProps {
  isOpen: boolean
  storyboardType: string
  onClose: () => void
  onSelectAd: (ad: FbAd, videoUrl: string) => void
  onUpload: (file: File) => void
  onPasteUrl: (url: string) => void
}

// ─── Helper Components ────────────────────────────────────────────────────────

function isVideoUrl(url: string | null): boolean {
  if (!url) return false
  const videoExts = ['.mp4', '.webm', '.mov', '.m3u8']
  const lowerUrl = url.toLowerCase()
  return videoExts.some((ext) => lowerUrl.includes(ext)) || lowerUrl.includes('video')
}

function AdThumbnail({ ad, selected, onClick }: { ad: FbAd; selected: boolean; onClick: () => void }) {
  const [imageError, setImageError] = useState(false)
  const primaryMedia = ad.media_urls?.[0] || null
  const isVideo = isVideoUrl(primaryMedia)

  return (
    <button
      onClick={onClick}
      className={`relative group rounded-xl overflow-hidden border-2 transition-all ${
        selected
          ? 'border-blue-500 ring-2 ring-blue-200'
          : 'border-transparent hover:border-gray-300'
      }`}
    >
      <div className="aspect-video bg-gray-100 relative">
        {!primaryMedia || imageError ? (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200">
            <Play className="w-8 h-8 text-gray-400" />
          </div>
        ) : isVideo ? (
          <div className="relative w-full h-full">
            <video
              src={primaryMedia}
              className="w-full h-full object-cover"
              muted
              preload="metadata"
              onError={() => setImageError(true)}
            />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-10 h-10 rounded-full bg-black/40 flex items-center justify-center group-hover:bg-black/60 transition-colors">
                <Play className="w-5 h-5 text-white fill-white" />
              </div>
            </div>
          </div>
        ) : (
          <img
            src={primaryMedia}
            alt={ad.page_name}
            className="w-full h-full object-cover"
            onError={() => setImageError(true)}
          />
        )}

        {selected && (
          <div className="absolute top-2 right-2 w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center">
            <CheckCircle2 className="w-4 h-4 text-white" />
          </div>
        )}
      </div>

      <div className="p-3 bg-white">
        <div className="flex items-center gap-2 mb-1">
          <div className="w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center text-[10px] font-semibold text-blue-600">
            {ad.page_name?.charAt(0).toUpperCase() || 'A'}
          </div>
          <span className="text-xs font-medium text-tf-text truncate">{ad.page_name || 'Unknown'}</span>
        </div>
        {ad.ad_text && (
          <p className="text-[11px] text-tf-text-muted line-clamp-2">{ad.ad_text}</p>
        )}
      </div>
    </button>
  )
}

// ─── Main Component ───────────────────────────────────────────────────────────

export function AdSourceModal({
  isOpen,
  storyboardType,
  onClose,
  onSelectAd,
  onUpload,
  onPasteUrl,
}: AdSourceModalProps) {
  const [activeTab, setActiveTab] = useState<TabId>('browse')
  const [ads, setAds] = useState<FbAd[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedAd, setSelectedAd] = useState<FbAd | null>(null)

  // Upload tab state
  const [urlInput, setUrlInput] = useState('')
  const [dragOver, setDragOver] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Fetch ads when modal opens
  useEffect(() => {
    if (isOpen && activeTab === 'browse') {
      fetchAds()
    }
  }, [isOpen, activeTab])

  const fetchAds = async () => {
    setLoading(true)
    setError(null)
    try {
      const allAds = await fbAdsService.listAds()
      // Filter to only show video ads
      const videoAds = allAds.filter(
        (ad) => ad.media_type === 'video' || ad.media_urls?.some(isVideoUrl)
      )
      setAds(videoAds)
    } catch (err) {
      console.error('Failed to fetch ads:', err)
      setError('Failed to load ads. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const filteredAds = ads.filter((ad) => {
    if (!searchTerm) return true
    const term = searchTerm.toLowerCase()
    return (
      ad.page_name?.toLowerCase().includes(term) ||
      ad.ad_text?.toLowerCase().includes(term)
    )
  })

  const handleSelectAd = () => {
    if (!selectedAd) return
    const videoUrl = selectedAd.media_urls?.find(isVideoUrl) || selectedAd.media_urls?.[0] || ''
    onSelectAd(selectedAd, videoUrl)
  }

  const handleFileSelect = (file: File) => {
    if (!file.type.startsWith('video/')) {
      setError('Please select a video file')
      return
    }
    onUpload(file)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(false)
    const file = e.dataTransfer.files[0]
    if (file) handleFileSelect(file)
  }

  const handleUrlSubmit = () => {
    if (!urlInput.trim()) return
    onPasteUrl(urlInput.trim())
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="relative w-full max-w-4xl max-h-[90vh] rounded-2xl bg-white shadow-2xl flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-tf-border">
          <div>
            <h2 className="text-lg font-semibold text-tf-text">Select Benchmark Ad</h2>
            <p className="text-sm text-tf-text-muted mt-0.5">
              Choose an ad to use as your {storyboardType.replace('-', ' ')} baseline
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-tf-border px-6">
          <button
            onClick={() => setActiveTab('browse')}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'browse'
                ? 'text-blue-600 border-blue-600'
                : 'text-tf-text-muted border-transparent hover:text-tf-text'
            }`}
          >
            Browse Ads
          </button>
          <button
            onClick={() => setActiveTab('upload')}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'upload'
                ? 'text-blue-600 border-blue-600'
                : 'text-tf-text-muted border-transparent hover:text-tf-text'
            }`}
          >
            Upload / Paste URL
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          {/* Browse Tab */}
          {activeTab === 'browse' && (
            <div className="space-y-4">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-tf-text-muted" />
                <input
                  type="text"
                  placeholder="Search ads by brand or text..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-tf-border bg-tf-bg-muted text-sm placeholder:text-tf-text-muted focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* Loading State */}
              {loading && (
                <div className="flex flex-col items-center justify-center py-16">
                  <Loader2 className="w-8 h-8 text-blue-600 animate-spin mb-3" />
                  <p className="text-sm text-tf-text-muted">Loading ads...</p>
                </div>
              )}

              {/* Error State */}
              {error && !loading && (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <AlertCircle className="w-10 h-10 text-red-500 mb-3" />
                  <p className="text-sm text-red-600 mb-4">{error}</p>
                  <Button variant="secondary" onClick={fetchAds}>
                    Try Again
                  </Button>
                </div>
              )}

              {/* Empty State */}
              {!loading && !error && filteredAds.length === 0 && (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <ImageIcon className="w-12 h-12 text-tf-text-muted mb-3" />
                  <p className="text-tf-text font-medium mb-1">No video ads found</p>
                  <p className="text-sm text-tf-text-muted">
                    {searchTerm ? 'Try a different search term' : 'Add competitors to see their ads'}
                  </p>
                </div>
              )}

              {/* Ads Grid */}
              {!loading && !error && filteredAds.length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {filteredAds.slice(0, 12).map((ad) => (
                    <AdThumbnail
                      key={ad.ad_id}
                      ad={ad}
                      selected={selectedAd?.ad_id === ad.ad_id}
                      onClick={() => setSelectedAd(ad)}
                    />
                  ))}
                </div>
              )}

              {filteredAds.length > 12 && (
                <p className="text-center text-sm text-tf-text-muted">
                  Showing 12 of {filteredAds.length} ads. Use search to find more.
                </p>
              )}
            </div>
          )}

          {/* Upload Tab */}
          {activeTab === 'upload' && (
            <div className="space-y-6">
              {/* File Upload */}
              <div>
                <h3 className="text-sm font-medium text-tf-text mb-3">Upload Video</h3>
                <div
                  onDragOver={(e) => {
                    e.preventDefault()
                    setDragOver(true)
                  }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={handleDrop}
                  className={`rounded-xl border-2 border-dashed p-8 text-center transition-colors ${
                    dragOver
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-tf-border bg-tf-bg-muted hover:border-gray-300'
                  }`}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="video/*"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0]
                      if (file) handleFileSelect(file)
                    }}
                  />
                  <div className="mx-auto w-14 h-14 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center mb-4">
                    <Upload className="w-7 h-7" />
                  </div>
                  <p className="text-tf-text font-medium mb-1">Drop your video here</p>
                  <p className="text-sm text-tf-text-muted mb-4">or click to browse</p>
                  <Button
                    variant="secondary"
                    onClick={() => fileInputRef.current?.click()}
                    icon={<FileVideo className="w-4 h-4" />}
                  >
                    Choose Video
                  </Button>
                  <p className="text-xs text-tf-text-muted mt-3">
                    Supported formats: MP4, MOV, WebM. Max 100MB
                  </p>
                </div>
              </div>

              {/* Divider */}
              <div className="flex items-center gap-4">
                <div className="flex-1 h-px bg-tf-border" />
                <span className="text-xs font-medium text-tf-text-muted uppercase">or</span>
                <div className="flex-1 h-px bg-tf-border" />
              </div>

              {/* URL Input */}
              <div>
                <h3 className="text-sm font-medium text-tf-text mb-3">Paste Video URL</h3>
                <div className="flex gap-3">
                  <div className="flex-1 relative">
                    <Link className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-tf-text-muted" />
                    <input
                      type="url"
                      placeholder="https://example.com/video.mp4"
                      value={urlInput}
                      onChange={(e) => setUrlInput(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleUrlSubmit()}
                      className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-tf-border text-sm placeholder:text-tf-text-muted focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <Button
                    variant="primary"
                    onClick={handleUrlSubmit}
                    disabled={!urlInput.trim()}
                  >
                    Use URL
                  </Button>
                </div>
                <p className="text-xs text-tf-text-muted mt-2">
                  Direct link to MP4, WebM, or other video formats
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer - only for browse tab with selection */}
        {activeTab === 'browse' && selectedAd && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-tf-border bg-tf-bg-muted">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <Play className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-tf-text">{selectedAd.page_name}</p>
                <p className="text-xs text-tf-text-muted">Selected ad</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button variant="secondary" onClick={() => setSelectedAd(null)}>
                Clear
              </Button>
              <Button variant="primary" onClick={handleSelectAd}>
                Use This Ad
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default AdSourceModal
